/**
 * EditPageLayout - Modern Edit Page Layout with Enhanced UX
 *
 * Features:
 * - Breadcrumb navigation with status indicator
 * - Tab-based content organization with progress indicator
 * - Floating sticky action bar with keyboard shortcuts
 * - Unsaved changes detection
 * - Responsive design for all screen sizes
 * - Smooth micro-interactions
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '../ui/tabs';
import { Card, CardContent } from '../ui/card';
import { Button } from '../base';
import { Badge } from '../ui/badge';
import {
	CheckCircle2,
	AlertCircle,
	Clock,
	ChevronRight,
} from 'lucide-react';

export interface IFormSection {
	id: string;
	label: string;
	icon?: ReactNode;
	badge?: string | number;
	badgeVariant?: 'default' | 'secondary' | 'destructive' | 'outline' | 'success';
	content: ReactNode;
	errorCount?: number;
}

export interface IEditPageLayoutProps {
	title: string;
	description?: string;
	tabs: IFormSection[];
	actions?: React.ReactNode;
	isLoading?: boolean;
	isSaving?: boolean;
	onSave?: () => void;
	onCancel?: () => void;
	className?: string;

	/** Breadcrumb items for navigation context */
	breadcrumbs?: {
		label: string;
		href?: string;
	}[];

	/** Current status of the record (e.g., 'Bản nháp', 'Đã xuất bản') */
	status?: string;
	statusVariant?: 'default' | 'secondary' | 'destructive' | 'success' | 'warning';

	/** Last modified timestamp */
	lastModified?: Date | string;

	/** Whether there are unsaved changes */
	hasUnsavedChanges?: boolean;

	/** Keyboard shortcut for save (default: Cmd/Ctrl + S) */
	saveShortcut?: string;
}

export const EditPageLayout = ({
	title,
	description,
	tabs,
	actions,
	isLoading = false,
	isSaving = false,
	onSave,
	onCancel,
	className,
	breadcrumbs,
	status,
	statusVariant = 'secondary',
	lastModified,
	hasUnsavedChanges = false,
	saveShortcut = '⌘S',
}: IEditPageLayoutProps) => {
	const [activeTab, setActiveTab] = useState(tabs[0]?.id || '');
	const [isTabTransitioning, setIsTabTransitioning] = useState(false);
	const [justSaved, setJustSaved] = useState(false);
	const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
	const wasSavingRef = useRef(false);

	// Calculate active tab index for progress indicator
	const activeTabIndex = useMemo(() => {
		return tabs.findIndex((tab) => tab.id === activeTab);
	}, [tabs, activeTab]);

	// Format last modified time
	const formattedLastModified = useMemo(() => {
		if (!lastModified) return null;
		const date = typeof lastModified === 'string' ? new Date(lastModified) : lastModified;
		return new Intl.DateTimeFormat('vi-VN', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		}).format(date);
	}, [lastModified]);

	// Keyboard shortcut handler
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if ((e.metaKey || e.ctrlKey) && e.key === 's') {
				e.preventDefault();
				if (onSave && !isSaving && !isLoading) {
					onSave();
				}
			}
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [onSave, isSaving, isLoading]);

	// Handle tab change with transition
	const handleTabChange = useCallback((value: string) => {
		setIsTabTransitioning(true);
		setTimeout(() => {
			setActiveTab(value);
			setIsTabTransitioning(false);
		}, 150);
	}, []);

	// Scroll active tab into view on mount
	useEffect(() => {
		const activeButton = tabRefs.current.get(activeTab);
		activeButton?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
	}, [activeTab]);

	useEffect(() => {
		if (isSaving) {
			wasSavingRef.current = true;
			return;
		}
		if (!isSaving && wasSavingRef.current) {
			setJustSaved(true);
			wasSavingRef.current = false;
			const timer = window.setTimeout(() => setJustSaved(false), 1200);
			return () => window.clearTimeout(timer);
		}
	}, [isSaving]);

	return (
		<div className={cn('min-h-screen flex flex-col bg-muted/20', className)}>
			{/* Header with Breadcrumb */}
			<header className='bg-background/95 backdrop-blur-sm border-b border-border/50 sticky top-0 z-40'>
				<div className='container mx-auto px-4 lg:px-6'>
					{/* Breadcrumb Navigation */}
					{breadcrumbs && breadcrumbs.length > 0 && (
						<div className='pt-4 pb-2'>
							<nav aria-label='breadcrumb'>
								<ol className='flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground'>
									{breadcrumbs.map((crumb, index) => (
										<li key={index} className='flex items-center gap-1.5'>
											{crumb.href ? (
												<a
													href={crumb.href}
													className='text-muted-foreground hover:text-foreground transition-colors'
												>
													{crumb.label}
												</a>
											) : (
												<span className={cn(
													'text-sm',
													index === breadcrumbs.length - 1 ? 'font-medium text-foreground' : 'text-muted-foreground'
												)}>
													{crumb.label}
												</span>
											)}
											{index < breadcrumbs.length - 1 && (
												<ChevronRight className='h-4 w-4 text-muted-foreground/50' />
											)}
										</li>
									))}
								</ol>
							</nav>
						</div>
					)}

					{/* Title Row */}
					<div className='flex flex-col gap-4 py-4 lg:flex-row lg:items-start lg:justify-between'>
						<div className='flex-1 min-w-0'>
							<div className='flex flex-wrap items-center gap-3'>
								<h1 className='text-2xl font-bold tracking-tight truncate'>{title}</h1>
								{status && (
									<Badge variant={statusVariant} className='shrink-0'>
										{status}
									</Badge>
								)}
								{hasUnsavedChanges && (
									<span className='inline-flex items-center gap-1 text-sm text-amber-600 dark:text-amber-500'>
										<AlertCircle className='h-4 w-4' />
										<span className='hidden sm:inline'>Chưa lưu</span>
									</span>
								)}
							</div>
							<div className='flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-sm text-muted-foreground'>
								{description && <span>{description}</span>}
								{formattedLastModified && (
									<span className='inline-flex items-center gap-1.5'>
										<Clock className='h-3.5 w-3.5' />
										Lần cuối: {formattedLastModified}
									</span>
								)}
							</div>
						</div>

						{/* Header Actions */}
						<div className='flex items-center gap-3 shrink-0'>
							{actions && (
								<div className='flex items-center gap-2'>
									{actions}
								</div>
							)}
						</div>
					</div>
				</div>
				<div className='bg-background border-b border-border/50'>
				<div className='container mx-auto px-4 lg:px-6'>
					<div className='relative'>
						<Tabs value={activeTab} onValueChange={handleTabChange} className='w-full'>
								<TabsList className='w-full justify-start h-auto bg-transparent p-0 border-b rounded-none mb-0 overflow-x-auto scrollbar-hide'>
									{tabs.map((tab) => (
									<TabsTrigger
										key={tab.id}
										ref={(el) => {
											if (el) tabRefs.current.set(tab.id, el);
										}}
										value={tab.id}
										className={cn(
											'relative flex items-center gap-2 px-4 py-3 rounded-none border-b-2 border-transparent',
											'mb-[-1px] data-[state=active]:border-primary data-[state=active]:bg-transparent',
											'data-[state=active]:shadow-none whitespace-nowrap',
											'text-muted-foreground hover:text-foreground hover:bg-muted/30',
											'data-[state=active]:text-foreground data-[state=active]:font-medium',
											'transition-all duration-200 ease-out',
											tab.errorCount && tab.errorCount > 0 && 'text-destructive data-[state=active]:text-destructive data-[state=active]:border-destructive'
										)}
									>
										{tab.icon && (
											<span className={cn(
												'transition-transform duration-200',
												activeTab === tab.id && 'scale-110'
											)}>
												{tab.icon}
											</span>
										)}
										<span>{tab.label}</span>
										{tab.badge !== undefined && (
											<Badge
												variant={tab.errorCount && tab.errorCount > 0 ? 'destructive' : (tab.badgeVariant || 'secondary')}
												className={cn(
													'text-xs px-1.5 py-0.5 min-w-[1.25rem] h-5 justify-center',
													tab.errorCount && tab.errorCount > 0 && 'animate-pulse'
												)}
											>
												{tab.badge}
											</Badge>
										)}
									</TabsTrigger>
								))}
							</TabsList>

							{/* Progress Indicator */}
							<div className='absolute bottom-0 left-0 right-0 h-0.5 bg-muted/50'>
								<div
									className='h-full bg-primary transition-all duration-300 ease-out'
									style={{
										width: `${((activeTabIndex + 1) / tabs.length) * 100}%`,
									}}
								/>
							</div>
						</Tabs>
						
					</div>
				</div>
			</div>
			</header>

			{/* Tab Navigation with Progress Indicator */}
			

			{/* Main Content Area */}
			<main className='flex-1'>
				<div className='container mx-auto px-4 lg:px-6 py-6'>
					<Tabs value={activeTab} onValueChange={handleTabChange}>
						{tabs.map((tab) => (
							<TabsContent
								key={tab.id}
								value={tab.id}
								className={cn(
									'mt-4 focus-visible:outline-none',
									'animate-in fade-in-0 slide-in-from-bottom-2 duration-200',
									isTabTransitioning && 'opacity-0'
								)}
							>
								<Card className='border border-border/50 shadow-sm bg-background'>
									<CardContent className='p-6'>
										{tab.content}
									</CardContent>
								</Card>
							</TabsContent>
						))}
					</Tabs>
				</div>
			</main>

			{/* Floating Sticky Action Bar */}
			<div className='sticky bottom-0 z-40'>
				{/* Backdrop blur effect */}
				<div className='h-2 bg-gradient-to-t from-muted/50 to-transparent' />

				<div className='bg-background/80 backdrop-blur-lg border-t border-border/50 shadow-lg'>
					<div className='container mx-auto px-4 lg:px-6'>
						<div className='flex items-center justify-between gap-4 py-4'>
							{/* Left side - Status & Shortcuts */}
							<div className='flex items-center gap-4'>
								<div className='hidden md:flex items-center gap-3 text-sm text-muted-foreground'>
									{hasUnsavedChanges ? (
										<span className='inline-flex items-center gap-1.5 text-amber-600 dark:text-amber-500'>
											<AlertCircle className='h-4 w-4' />
											Thay đổi chưa được lưu
										</span>
									) : (
										<span aria-live='polite' className={cn('inline-flex items-center gap-1.5 text-emerald-600 dark:text-emerald-500', justSaved && 'ux-save-success rounded-lg px-2 py-1')}>
											<CheckCircle2 className='h-4 w-4' />
											Tất cả thay đổi đã được lưu
										</span>
									)}
								</div>

								{/* Keyboard Shortcut Hint */}
								<div className='hidden lg:flex items-center gap-2'>
									<kbd className='inline-flex items-center justify-center px-2 py-1 text-xs font-semibold bg-muted rounded border border-border/50 text-muted-foreground'>
										{saveShortcut.includes('⌘') ? '⌘' : 'Ctrl'}
									</kbd>
									<kbd className='inline-flex items-center justify-center px-2 py-1 text-xs font-semibold bg-muted rounded border border-border/50 text-muted-foreground'>
										S
									</kbd>
								</div>
							</div>

							{/* Right side - Action Buttons */}
							<div className='flex items-center gap-3'>
								{onCancel && (
									<Button
										theme='secondary'
										preset='outline'
										onClick={onCancel}
										isDisabled={isSaving || isLoading}
										text='Hủy bỏ'
										sizeClass='md'
										className='min-w-[100px]'
									/>
								)}
								{onSave && (
									<Button
										theme='primary'
										preset='default'
										onClick={onSave}
										isLoading={isSaving}
										isDisabled={isSaving || isLoading}
										text={isSaving ? 'Đang lưu...' : 'Lưu thay đổi'}
										sizeClass='md'
										className='min-w-[120px]'
									/>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default EditPageLayout;
